import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { Loader2 } from "lucide-react"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/useAuth"
import { getAssignees, createIssue, refreshIssuesCache, type GitlabAssignee } from "@/services/gitlabService"
import type { ValidationFailurePoint } from "@/services/parasiteRateService"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RaiseGitlabIssueDialogProps {
  /** Whether the dialog is currently open. */
  open: boolean
  /**
   * Callback fired when the dialog requests to change its open state (e.g. the
   * user presses Escape, clicks the overlay, or the close button).
   *
   * @param open - The requested next open state.
   */
  onOpenChange: (open: boolean) => void
  /**
   * The validation failure point the user clicked on. Its country and source
   * id identify the group of failures this issue covers.
   */
  point: ValidationFailurePoint
  /**
   * The full set of validation failure points currently loaded.
   */
  allValidationFailures: ValidationFailurePoint[]
}

/**
 * RaiseGitlabIssueDialog is the modal shown when a user chooses to raise a
 * GitLab issue for a parasite rate validation failure.
 *
 * @param props - The open state, open-change handler, the clicked point, and
 *   the full list of validation failures used for aggregation.
 * @returns The dialog element.
 */
export default function RaiseGitlabIssueDialog({
  open,
  onOpenChange,
  point,
  allValidationFailures,
}: RaiseGitlabIssueDialogProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Selected assignee id (as a string, since the Select works with strings).
  // Empty string means "unassigned".
  const [assigneeId, setAssigneeId] = useState<string>("")
  const [additionalNotes, setAdditionalNotes] = useState<string>("")

  // Assignee list state, loaded from the GitLab proxy when the dialog opens.
  const [assignees, setAssignees] = useState<GitlabAssignee[]>([])
  const [assigneesLoading, setAssigneesLoading] = useState<boolean>(false)
  const [assigneesError, setAssigneesError] = useState<string | null>(null)

  // Whether an issue-creation request is in flight.
  const [isCreating, setIsCreating] = useState<boolean>(false)

  const countryName: string | null = point.country
  const sourceId: string | null = point.source_id1

  /**
   * All validation failures that belong to the same country and source id as
   * the clicked point. These are the failures the issue is being raised for.
   */
  const matchingFailures: ValidationFailurePoint[] = useMemo(() => {
    return allValidationFailures.filter(
      (candidate) => candidate.country === countryName && candidate.source_id1 === sourceId,
    )
  }, [allValidationFailures, countryName, sourceId])

  // Issue title, e.g. 'PR Validation Failure: Kenya 12345'. Missing values
  // fall back to "Unknown" rather than silently producing a malformed title.
  const issueTitle: string = `PR Validation Failure: ${countryName ?? "Unknown"} ${sourceId ?? "Unknown"}`

  /**
   * The issue description: one entry per matching validation failure listing
   * its validation_error text, the point coordinates, and its MAP survey id
   * when present.
   */
  const description: string = useMemo(() => {
    const lines: string[] = matchingFailures.map((failure) => {
      const parts: string[] = []

      const failureText: string | null = failure.validation_error
      const trimmedFailureText: string = failureText != null && failureText.trim() !== "" ? failureText.trim() : "(no validation message)"

      // Append the point's coordinates in brackets after the failure message so
      // the issue identifies exactly which cluster failed. Only appended when
      // both coordinates are present.
      const latitude: number | null = failure.latitude
      const longitude: number | null = failure.longitude
      const coordinatesAvailable: boolean = latitude != null && longitude != null
      const failureLine: string = coordinatesAvailable
        ? `${trimmedFailureText} (${latitude}, ${longitude})`
        : trimmedFailureText
      parts.push(failureLine)

      // Only include the MAP survey id when the failure actually has one.
      const mapSurveyId: string | null = failure.map_survey_id
      if (mapSurveyId != null && mapSurveyId.trim() !== "") {
        parts.push(`MAP Survey ID: ${mapSurveyId.trim()}`)
      }

      return `- ${parts.join(" | ")}`
    })

    return lines.join("\n")
  }, [matchingFailures])

  /**
   * Load the assignable GitLab members. Called when the dialog opens so the
   * dropdown is populated by the time the user looks at it.
   */
  const loadAssignees = useCallback(async (): Promise<void> => {
    try {
      setAssigneesLoading(true)
      setAssigneesError(null)

      const token: string = await getAccessToken()
      const members: GitlabAssignee[] = await getAssignees(token)
      setAssignees(members)
    } catch (error) {
      const message: string = error instanceof Error ? error.message : "Failed to load assignees"
      console.error("RaiseGitlabIssueDialog: failed to load assignees:", error)
      setAssigneesError(message)
    } finally {
      setAssigneesLoading(false)
    }
  }, [getAccessToken])

  // Load assignees whenever the dialog transitions to open.
  useEffect(() => {
    if (open) {
      loadAssignees()
    }
  }, [open, loadAssignees])

  /**
   * Build the full issue description sent to GitLab. The user's free-text
   * additional notes (when present) are appended under their own heading below
   * the aggregated validation failures.
   *
   * @returns The combined description string.
   */
  const buildIssueDescription = (): string => {
    const trimmedNotes: string = additionalNotes.trim()
    if (trimmedNotes === "") {
      return description
    }
    return `${description}\n\n## Additional notes\n\n${trimmedNotes}`
  }

  /**
   * Handle a click on Create. Submits the issue to the backend GitLab proxy,
   * shows a success/error toast, and closes the dialog on success.
   *
   * @returns Nothing. Side effects only (network call, toast, close).
   */
  const handleCreate = async (): Promise<void> => {
    try {
      setIsCreating(true)

      const token: string = await getAccessToken()
      const result = await createIssue(token, {
        title: issueTitle,
        description: buildIssueDescription(),
        // An assignee is required: the Create button is disabled until one is
        // selected, so assigneeId is always a valid numeric id here.
        assigneeId: Number(assigneeId),
      })

      // Refresh the shared issues cache so the cluster's button immediately
      // switches from "Raise GitLab issue" to "Go to GitLab ticket" using the
      // newly created issue.
      try {
        await refreshIssuesCache(token)
      } catch (refreshError) {
        console.error("RaiseGitlabIssueDialog: failed to refresh issues cache:", refreshError)
      }

      toast.success(`Created GitLab issue #${result.iid}`)
      onOpenChange(false)
    } catch (error) {
      const message: string = error instanceof Error ? error.message : "Failed to create the GitLab issue"
      console.error("RaiseGitlabIssueDialog: failed to create issue:", error)
      toast.error(message)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Creating issue called "{issueTitle}"</DialogTitle>
          <DialogDescription asChild>
            <div>
              <p className="mb-1 font-medium text-foreground">
                Description:
              </p>
              {matchingFailures.length > 0 ? (
                <pre className="max-h-40 overflow-y-auto rounded-md bg-muted p-2 text-xs whitespace-pre-wrap break-words">
                  {description}
                </pre>
              ) : (
                <p className="text-xs">No matching validation failures found.</p>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <label htmlFor="gitlab-issue-assignee" className="text-sm font-medium">
            Assign issue
          </label>
          <Select
            value={assigneeId}
            onValueChange={setAssigneeId}
            disabled={assigneesLoading || assignees.length === 0}
          >
            <SelectTrigger id="gitlab-issue-assignee" className="w-full">
              <SelectValue
                placeholder={assigneesLoading ? "Loading assignees..." : "Assign issue"}
              />
            </SelectTrigger>
            <SelectContent>
              {assignees.map((member) => (
                <SelectItem key={member.id} value={String(member.id)}>
                  {member.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {assigneesError != null && (
            <p className="text-xs text-destructive">{assigneesError}</p>
          )}
        </div>

        {/* Free-text additional notes the user can add to the issue. */}
        <div className="flex flex-col gap-2">
          <label htmlFor="gitlab-issue-additional-notes" className="text-sm font-medium">
            Additional notes
          </label>
          <Textarea
            id="gitlab-issue-additional-notes"
            value={additionalNotes}
            onChange={(event) => setAdditionalNotes(event.target.value)}
            placeholder="Add any additional notes..."
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isCreating}>Cancel</Button>
          </DialogClose>
          <Button
            type="button"
            onClick={handleCreate}
            disabled={isCreating || matchingFailures.length === 0 || assigneeId === ""}
          >
            {isCreating && <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />}
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
